import type {
  ApiError,
  ConfirmRequest,
  ConfirmResult,
  PublicCheckoutSession,
  PublicPaymentMethod,
} from "./types";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "/v1";
const DEFAULT_TIMEOUT = 12_000;

export class CheckoutApiError extends Error {
  statusCode: number;
  code?: string;
  constructor(statusCode: number, message: string, code?: string) {
    super(message);
    this.statusCode = statusCode;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), init.timeoutMs ?? DEFAULT_TIMEOUT);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      signal: init.signal ?? controller.signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(init.headers ?? {}),
      },
      credentials: "omit",
      cache: "no-store",
      redirect: "manual",
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new CheckoutApiError(0, "Tiempo de espera agotado. Probá de nuevo.");
    }
    throw new CheckoutApiError(0, "No se pudo conectar con el servidor.");
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const err = (body as { error?: ApiError } | null)?.error;
    throw new CheckoutApiError(
      res.status,
      err?.message ?? `Error ${res.status}`,
      err?.code,
    );
  }

  return body as T;
}

export const checkoutApi = {
  getSession(publicToken: string, signal?: AbortSignal): Promise<PublicCheckoutSession> {
    return request<PublicCheckoutSession>(`/public/checkout-sessions/${encodeURIComponent(publicToken)}`, {
      method: "GET",
      signal,
    });
  },
  paymentMethods(publicToken: string, signal?: AbortSignal): Promise<PublicPaymentMethod[]> {
    return request<PublicPaymentMethod[]>(
      `/public/checkout-sessions/${encodeURIComponent(publicToken)}/payment-methods`,
      { method: "GET", signal },
    );
  },
  confirm(publicToken: string, payload: ConfirmRequest, signal?: AbortSignal): Promise<ConfirmResult> {
    return request<ConfirmResult>(`/public/checkout-sessions/${encodeURIComponent(publicToken)}/confirm`, {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    });
  },
};
